import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    model,
    output,
    signal,
    untracked,
} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ConfirmationDialogService} from '@brunata-angular/ui';
import {browserTimeZone, TranslatePipe, TranslateService} from '@brunata-angular/util';
import {
    SavedExport,
    SavedExportCreateRequest,
    savedExportFileFormat,
    savedExportScheduleFields,
    savedExportScheduleValue,
    toSavedExportCreateRequest,
} from '../saved-export.model';
import {exportFileFormatFormValue} from '../../shared/models/export-file-format.model';
import {flowSlots} from '../../shared/models/export-flow-slots.model';
import {quickFlowSteps} from '../../quick-exports/quick-exports.model';
import {ActionButtonComponent} from '../../shared/ui/action-button/action-button.component';
import {ExportTestButtonComponent} from '../../shared/ui/export-test-button/export-test-button.component';
import {
    QuickExportsFormService,
    quickExportsFormProviders,
} from '../../quick-exports/quick-exports-form.service';
import {exportFormats} from '../../shared/models/output-format.model';
import {StepFlowComponent} from '../../steps/step-flow/step-flow.component';

@Component({
    selector: 'app-edit-saved-export-modal',
    standalone: true,
    imports: [TranslatePipe, StepFlowComponent, ExportTestButtonComponent, ActionButtonComponent],
    templateUrl: './edit-saved-export-modal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    // Reuse the quick-export wizard's form store so the shared step components render here exactly
    // as they do in the create flow.
    providers: quickExportsFormProviders,
})
export class EditSavedExportModalComponent {
    // NgbModal has no setInput, so the opener pushes the row in via model().set() right after open.
    readonly savedExport = model.required<SavedExport>();
    // Emit the validated payload; the parent owns the HTTP call, close, reload and toast.
    readonly save = output<SavedExportCreateRequest>();
    readonly isSaving = signal(false);

    private readonly activeModal = inject(NgbActiveModal);
    protected readonly formService = inject(QuickExportsFormService);

    protected readonly isScheduled = computed(() => this.savedExport().recurrence !== null);

    protected readonly steps = computed(() =>
        quickFlowSteps(
            flowSlots(['name'], {
                without: this.isScheduled() ? ['columns'] : ['columns', 'schedule'],
            }),
            this.formService,
        ),
    );

    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly translateService = inject(TranslateService);

    constructor() {
        effect(() => {
            const savedExport = this.savedExport();
            untracked(() => this.prefillForm(savedExport));
        });
        afterNextRender(() => this.formService.form.markAsPristine());
    }

    // patchValue / signal sets leave the form pristine, so cancelling an untouched edit skips the
    // discard warning.
    private prefillForm(savedExport: SavedExport): void {
        this.formService.selectMode('schedule');
        // On-demand rows keep the schedule group disabled, so validity only counts the fields the
        // modal renders.
        this.formService.scheduleGroupActive.set(this.isScheduled());
        const formatId = exportFormats.find(
            format => format.outputFormat === savedExport.outputFormat,
        )?.id;
        if (formatId != null) {
            this.formService.selectFormatById(formatId);
        }
        this.formService.storedColumnConfig.set({
            headerRow: savedExport.headerRow,
            unitPlacement: savedExport.unitPlacement,
            columns: savedExport.columns,
        });
        this.formService.fileFormat.patchValue(
            exportFileFormatFormValue(savedExportFileFormat(savedExport)),
        );
        this.formService.form.patchValue({
            name: savedExport.name,
            timeZone: savedExport.timeZone ?? browserTimeZone(),
            schedule: savedExportScheduleValue(savedExport),
        });
        this.formService.sftpConnectionRemoval.load(savedExport);
    }

    protected onSave(): void {
        const {outputFormat} = this.formService.selectedFormat();
        const form = this.formService.form;
        const {name, schedule, timeZone} = form.getRawValue();
        if (form.invalid || name === null) {
            form.markAllAsTouched();
            return;
        }
        // Omitting these would make the update replace the stored file shape with the default.
        const savedExport = this.savedExport();
        this.save.emit({
            ...toSavedExportCreateRequest(savedExport),
            ...savedExportScheduleFields(this.isScheduled(), schedule),
            ...this.formService.fileFormatSettings(),
            outputFormat,
            name,
            timeZone,
        });
    }

    protected cancel(): void {
        if (!this.formService.hasUnsavedChanges()) {
            this.activeModal.dismiss();
            return;
        }
        void this.confirmationDialogService
            .confirmWithSettings({
                title: this.translateService.translate('common_warning', 'online', 'common'),
                message: this.translateService.translate(
                    'savedexport_edit_cancel_warning',
                    'online',
                    'export',
                ),
                centered: true,
            })
            .then(confirmed => {
                if (confirmed) {
                    this.activeModal.dismiss();
                }
            });
    }
}
